import { useEffect, useMemo } from 'react'

import { CustomizationFieldFragment } from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'

import {
  ResourceType,
  subcategoriesForCategory,
} from 'modules/expenses/hooks/useExpensesCustomization'

interface SubcategorySelectorProps {
  category: string | undefined
  customizationFields: CustomizationFieldFragment[]
  resourceType: ResourceType
  value: string | undefined
  onSelectedSubcategory: (category: string) => void
  mandatory?: boolean
  readonly?: boolean
  error?: string
  asReviewer?: boolean
}

const SubcategorySelector = ({
  category,
  customizationFields,
  resourceType,
  value,
  onSelectedSubcategory,
  mandatory,
  readonly,
  error,
  asReviewer,
}: SubcategorySelectorProps) => {
  const subcategoryOptions = useMemo(
    () =>
      resourceType && category
        ? subcategoriesForCategory(customizationFields, resourceType, category).map((option) => {
            return { label: option.label, value: option.value ?? '' }
          })
        : [],
    [customizationFields, resourceType, category]
  )

  useEffect(() => {
    if (subcategoryOptions.length === 1 && !value && mandatory && !asReviewer) {
      const singleValue = subcategoryOptions[0]?.value
      if (singleValue !== undefined) {
        onSelectedSubcategory(singleValue)
      }
    }
  }, [subcategoryOptions, value, onSelectedSubcategory])

  if (subcategoryOptions.length === 0) {
    return null
  }
  return (
    <SelectField
      label={i18n.t('expenses_v2.forms.fields.subcategory.label')}
      none={i18n.t('expenses_v2.forms.fields.subcategory.placeholder')}
      options={subcategoryOptions}
      value={value}
      onChange={(subcategory) => onSelectedSubcategory(subcategory)}
      mandatory={mandatory}
      error={error}
      readonly={readonly}
    />
  )
}

export default SubcategorySelector
